// ============================================================
// The body plate's shared drawing pieces (plan body-map P2, P4, P8): the figure's line art as one reusable group,
// its glow, each location's clip, lines and hit area, the sights, the callout's leader, the life bar, the frame and
// the unavailable frame. Every plate - the hero plate, the vehicle, the goo, the thumbnail, the crowd's members -
// draws through these, so they cannot drift apart.
// ============================================================

import { memo, useId, type ReactNode, type KeyboardEvent } from 'react';
import type { PathGroup, PlateArt } from '../../engine/plate/figureArt';
import { calloutName, damageOf, type LocationMark } from '../../engine/plate/plateModel';
import { snowImage, SNOW_TILE } from '../../engine/plate/snow';
import type { Frame } from '../../engine/plate/calloutLayout';
import clsx from '../../utils/clsx';
import tx from '../../styles/PlateTraits.module.css';
import bodyStyles from '../../styles/BodyMap.module.css';
import styles from '../../styles/BodyPlate.module.css';
import fx from '../../styles/PlateFx.module.css';

/** The art is traced 2000 wide; this centres it in the 2123-wide frame the regions were fitted in. */
const ART_SHIFT = 'translate(61.5 0)';

/** A plate's frame (its viewBox), in the figure's units; the hero plate's is the layout's (engine/plate/calloutLayout). */
export type { Frame };

export const viewBoxOf = (f: Frame) => `${f.x} ${f.y} ${f.w} ${f.h}`;

/** A drawing's own id prefix (useId, cleaned for use inside `url(#...)`), so two plates on a page never share defs. */
export function usePlateUid(prefix: string) {
  return `${prefix}${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

/** A region's pieces ("x,y x,y ...") as one path. */
const piecesPath = (pieces: string[]) => pieces.map((p) => `M${p.trim().split(/\s+/).join(' L')} Z`).join(' ');

export interface Regions {
  /** What each location's lines are clipped to. */
  clip: Record<string, string>;
  /** What takes the pointer for it. */
  hit: Record<string, string>;
  /** The location that is whatever the drawn ones leave: a person's Body, a vehicle's Hull. */
  rest: string;
}

/** Built once per art: the art objects are cached per asset (plateArt), so every plate of a figure shares these. */
const regionsCache = new WeakMap<object, Regions>();

/**
 * Each location's clip and hit area. The drawn regions are their polygons (pieces may overlap: non-zero). The REST
 * is never drawn: its clip is everything but the drawn ones (even-odd over a frame far larger than the figure), and
 * its hit area is `restHit`, laid UNDER the drawn ones so they take the pointer where they overlap it. (Even-odd over
 * "box + the drawn" would also fill the parts of the drawn outside the box - the head's centre read as the Body.)
 */
function regionsFor(regions: Record<string, string[]>, rest: string, restHit: string): Regions {
  const hit = regionsCache.get(regions);
  if (hit) return hit;
  const drawn = Object.fromEntries(Object.entries(regions).map(([id, pieces]) => [id, piecesPath(pieces)]));
  const others = Object.values(drawn).join(' ');
  const built = {
    clip: { ...drawn, [rest]: `M-2000 -2000 H4200 V5000 H-2000 Z ${others}` },
    hit: { ...drawn, [rest]: restHit },
    rest,
  };
  regionsCache.set(regions, built);
  return built;
}

/** A person's: the Body is the rest, its hit area the Body's box. */
export function regionsOf(art: PlateArt): Regions {
  const [x0, y0, x1, y1] = art.box;
  return regionsFor(art.regions, 'body', `M${x0} ${y0} H${x1} V${y1} H${x0} Z`);
}

/** A vehicle's: the Hull is the rest, and takes the pointer anywhere in the frame the drawn locations do not. */
export const vehicleRegions = (art: { regions: Record<string, string[]> }) =>
  regionsFor(art.regions, 'hull', 'M0 0 H2123 V3000 H0 Z');

/** The rest first, so every drawn region lies over its hit area. */
export const hitOrder = <T extends { id: string }>(locations: T[], regions: Regions) =>
  [...locations].sort((a, b) => Number(b.id === regions.rest) - Number(a.id === regions.rest));

/** Each location's clip: the drawn ones by non-zero (their pieces may overlap), the rest a hole cut by even-odd. */
export function LocationClips({ u, regions }: { u: string; regions: Regions }) {
  return Object.entries(regions.clip).map(([id, d]) => (
    <clipPath key={id} id={`${u}-c-${id}`}>
      <path d={d} clipRule={id === regions.rest ? 'evenodd' : 'nonzero'} />
    </clipPath>
  ));
}

/**
 * A location's lines' look by its marks (plan plate-unboxed): a destroyed part's lines white and still, a crippled
 * part's dim under its snow (`Snow`), both outside the health ramp; otherwise plain.
 */
export function marksClass(marks: readonly LocationMark[]) {
  const hurt = damageOf(marks);
  return clsx(styles.loc, hurt === 'destroyed' && styles.destroyed, hurt === 'crippled' && styles.crippled);
}

/** The snow's tile, made once: it is the same for every plate. */
/** The noise tile, as one image (engine/plate/snow), made once. */
const SNOW_IMAGE = snowImage();

/**
 * The defs a crippled part's snow needs (plan plate-unboxed): the noise tile as a pattern, and a mask of the figure's
 * own lines thickened to its shape, so the snow fills the part and not the box round it.
 */
export const SnowDefs = memo(function SnowDefs({ u }: { u: string }) {
  return (
    <>
      {/* The tile's greys as a mask's light over one rect in the snow's colour: one image, not six hundred rects. */}
      <mask id={`${u}-snowtile`} maskUnits="userSpaceOnUse" x={0} y={0} width={SNOW_TILE} height={SNOW_TILE}>
        <image href={SNOW_IMAGE} width={SNOW_TILE} height={SNOW_TILE} className={styles.snowImage} />
      </mask>
      <pattern id={`${u}-snow`} width={SNOW_TILE} height={SNOW_TILE} patternUnits="userSpaceOnUse">
        <rect className={styles.snowCell} width={SNOW_TILE} height={SNOW_TILE} mask={`url(#${u}-snowtile)`} />
      </pattern>
      <mask id={`${u}-snowmask`} maskUnits="userSpaceOnUse" {...SNOW_MASK_RECT}>
        <use href={`#${u}-art`} className={styles.snowShape} />
      </mask>
    </>
  );
});

/** Room for the snow and its mask: well past any figure's frame. */
const SNOW_MASK_RECT = { x: -3000, y: -3000, width: 9000, height: 10000 };

/**
 * A crippled location's VHS snow: grey-white noise over the location's own shape (its clip, and the thickened lines'
 * mask), flickering in place at every band and frozen with motion off. Outside the ramp.
 */
export function Snow({ u, clip }: { u: string; clip: string }) {
  return (
    <g clipPath={`url(#${u}-c-${clip})`} mask={`url(#${u}-snowmask)`} className={styles.snow} data-snow={clip}>
      <rect className={styles.snowNoise} fill={`url(#${u}-snow)`} {...SNOW_MASK_RECT} />
    </g>
  );
}

/**
 * The glow's mask with a hole in each destroyed region (plan plate-unboxed): a destroyed part keeps no glow. Null
 * when nothing is destroyed, so the glow is drawn unmasked.
 */
export function GlowHole({ u, regions }: { u: string; regions: readonly Hole[] }) {
  if (regions.length === 0) return null;
  return (
    <mask id={`${u}-glowhole`} maskUnits="userSpaceOnUse" {...SNOW_MASK_RECT}>
      <rect className={styles.maskOpen} {...SNOW_MASK_RECT} />
      {regions.map((h) => (
        <path key={h.d} className={styles.maskShut} d={h.d} fillRule={h.evenodd ? 'evenodd' : 'nonzero'} />
      ))}
    </mask>
  );
}

/** A region cut from the glow: its path, and whether it is the rest (a hole cut by even-odd, as its clip is). */
export interface Hole {
  d: string;
  evenodd: boolean;
}

/** The glow's holes for these destroyed locations of a figure's (or a vehicle's) regions. */
export const holesOf = (regions: Regions, ids: readonly string[]): Hole[] =>
  ids.map((id) => ({ d: regions.clip[id], evenodd: id === regions.rest }));

/** The cover's AP beside an armour numeral: in the cover's blue, a size smaller, the armour left in ink (P5). */
export const CoverPlus = ({ ap }: { ap: number | null }) =>
  ap === null ? null : <tspan className={styles.coverPlus}>{`+${ap}`}</tspan>;

interface ArtDefsProps {
  u: string;
  /** The line art, and (for a crowd) the solid silhouette. */
  art: { art: PathGroup; silhouette?: PathGroup };
  /** Also define the figure's solid silhouette (`<u>-sil`): a crowd member fills it to hide those behind. */
  silhouette?: boolean;
  /** Also define the glow's filter (the plates that draw a `Glow`). */
  glow?: boolean;
  children?: ReactNode;
}

/** The figure's line art as one reusable group (`<u>-art`), plus the silhouette, the glow and any clips asked for. */
export function ArtDefs({ u, art, silhouette, glow, children }: ArtDefsProps) {
  return (
    <defs>
      <PathGroupDef id={`${u}-art`} group={art.art} />
      {silhouette && art.silhouette && <PathGroupDef id={`${u}-sil`} group={art.silhouette} />}
      {children}
      {glow && (
        <filter id={`${u}-glow`} x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
      )}
    </defs>
  );
}

/**
 * One traced path group, placed in the frame. Memoised: it can be hundreds of paths, and its group is one object per
 * asset, so a hover elsewhere on the plate never reconciles them.
 */
const PathGroupDef = memo(function PathGroupDef({ id, group }: { id: string; group: PathGroup }) {
  return (
    <g id={id} transform={ART_SHIFT}>
      <g transform={group.transform}>
        {group.paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </g>
    </g>
  );
});

/** The soft glow under the lines. */
export const Glow = ({ u, k = 1 }: { u: string; k?: number }) => (
  // The art's own multiplier (`PlateArt.glow`): dense machine art glows a third as much (plan design-gaps, P3).
  <g className={styles.glow} filter={`url(#${u}-glow)`} opacity={GLOW_OPACITY * k}>
    <use href={`#${u}-art`} fill="currentColor" />
  </g>
);

/** The glow's strength on a figure whose art asks for the full glow (the bench's 0.3). */
const GLOW_OPACITY = 0.3;

/** One location's lines: the art under its clip (or one piece's, `<id>-<k>`), in its marks' look. */
export function PlateLines({
  u,
  id,
  className,
  clip = id,
}: {
  u: string;
  id: string;
  className: string;
  clip?: string;
}) {
  return (
    <g className={className} clipPath={`url(#${u}-c-${clip})`} data-lines={id}>
      <use href={`#${u}-art`} fill="currentColor" />
    </g>
  );
}

interface HitProps {
  id: string;
  /** Its hit area. */
  d: string;
  onHot?: (id: string | null) => void;
  onContextMenu?: (id: string) => void;
  /** The pointer over it: the armour edit's by default, the sights' when they are live. */
  cursor?: string;
}

/**
 * One location's unseen hit area - line art alone catches the pointer only on its lines. Kept OUT of the lines'
 * group, so a copy of the lines (the lens) carries no hit area to swallow a click.
 */
export function PlateHit({ id, d, onHot, onContextMenu, cursor }: HitProps) {
  return (
    <g data-loc={id}>
      <path
        className={styles.hit}
        d={d}
        style={{ cursor: cursor ?? (onContextMenu ? 'context-menu' : 'default') }}
        onPointerEnter={onHot && (() => onHot(id))}
        onPointerLeave={onHot && (() => onHot(null))}
        onContextMenu={
          onContextMenu &&
          ((e) => {
            e.preventDefault();
            onContextMenu(id);
          })
        }
      />
    </g>
  );
}

export interface Targeting {
  selectedId: string;
  penalty: number;
  onEngageZone: (id: string) => void;
  onClear: () => void;
}

/** The lock-on's corner arms, in the figure's frame (the bench's 90). */
const BRACKET_ARM = 90;
/** Black Carapace: it presents less than its bulk - the brackets sit this much inside its box, each side. */
const TIGHT = 0.12;

/** A location's box drawn in by a share of its size on every side. */
function insetBox([x0, y0, x1, y1]: readonly number[], share: number): readonly number[] {
  const dx = (x1 - x0) * share;
  const dy = (y1 - y0) * share;
  return [x0 + dx, y0 + dy, x1 - dx, y1 - dy];
}

/** The location a live targeting is locked on, when this plate has it; null for auto or another body's location. */
export function lockedOn(locations: { id: string }[], targeting: Targeting | null): string | null {
  if (!targeting) return null;
  return locations.some((l) => l.id === targeting.selectedId) ? targeting.selectedId : null;
}

/** The look of a location's lines while another is locked: it steps back (the bench's `.locked .loc:not(.on)`). */
export const dimmedBy = (locked: string | null, id: string) =>
  locked !== null && locked !== id ? styles.dimmed : undefined;

interface SightsProps {
  /** The locations, in the order their hit areas stack (hitOrder). */
  locations: { id: string; name: string }[];
  /** A location's box in the plate's frame (`PlateFrame.partBox`). */
  boxOf: (id: string) => readonly number[];
  /** Its hit area: the sight takes it over while the sights are live, so the location itself is the button. */
  hitOf: (id: string) => string;
  targeting: Targeting;
  onHot?: (id: string | null) => void;
  onContextMenu?: (id: string) => void;
  /** Black Carapace: it presents less than its bulk - the brackets a size tighter (plan body-map T2). */
  tight?: boolean;
  /** Strange Physiology: there is no weak point to find - a locked bracket slides off. */
  slips?: boolean;
}

/**
 * The attacker's sights on a plate: the bench's LOCK-ON (ADR 2026-10-11). Each location wears corner brackets
 * fitted to its box, unseen until the pointer finds the location (CSS hover on the zone), closed by a click - the
 * called shot there, on the targeting console's own writers - while the other locations step back (`dimmedBy`).
 * The zone is the location's hit area, so it still reads the location out and takes the armour edit's right-click.
 */
export function PlateSights({ locations, boxOf, hitOf, targeting, onHot, onContextMenu, tight, slips }: SightsProps) {
  const locked = lockedOn(locations, targeting);
  return locations.map((l) => (
    <LockOn
      key={`sight-${l.id}`}
      id={l.id}
      name={l.name}
      d={boxBracketPath(tight ? insetBox(boxOf(l.id), TIGHT) : boxOf(l.id), 0, BRACKET_ARM)}
      hit={hitOf(l.id)}
      engaged={locked === l.id}
      slips={!!slips && locked === l.id}
      targeting={targeting}
      onHot={onHot}
      onContextMenu={onContextMenu}
    />
  ));
}

interface LockOnProps {
  id: string;
  name: string;
  /** The bracket's path. */
  d: string;
  /** The location's hit area. */
  hit: string;
  engaged: boolean;
  slips: boolean;
  targeting: Targeting;
  onHot?: (id: string | null) => void;
  onContextMenu?: (id: string) => void;
}

/** What a sight says of itself: its accessible name, and its tooltip. */
function sightWords(name: string, engaged: boolean, penalty: number) {
  if (engaged) return { label: `${name} - clear called shot`, title: `${name} - clear the called shot` };
  return { label: `${name} - called shot ${-penalty || 0}`, title: `${name} - call the shot here` };
}

/** Enter presses the sight; Space deliberately does nothing - the window keydown listener would ALSO fire the attack. */
const onEnter = (press: () => void) => (e: KeyboardEvent) => {
  if (e.key !== 'Enter') return;
  e.preventDefault();
  press();
};

/**
 * One location's sight: the button (its hit area and its bracket) and, locked on Strange Physiology, a copy of the
 * bracket that slides off while the bracket itself stays, dimmed, where the attacker clicks it - a moving bracket
 * would move its own click target away.
 */
function LockOn({ id, name, d, hit, engaged, slips, targeting, onHot, onContextMenu }: LockOnProps) {
  const press = () => (engaged ? targeting.onClear() : targeting.onEngageZone(id));
  const words = sightWords(name, engaged, targeting.penalty);
  return (
    <>
      <g className={slips ? tx.sightSlipped : undefined}>
        <g
          role="button"
          tabIndex={0}
          data-testid={`map-zone-${id}`}
          data-sight-zone
          aria-label={words.label}
          aria-pressed={engaged}
          className={styles.sightZone}
          onClick={press}
          onKeyDown={onEnter(press)}
        >
          <title>{`${words.title} (right-click sets armour)`}</title>
          <PlateHit id={id} d={hit} onHot={onHot} onContextMenu={onContextMenu} cursor="crosshair" />
          <path className={clsx(styles.sight, engaged && styles.sightLock)} d={d} />
        </g>
      </g>
      {slips && (
        <g className={tx.sightSlips} aria-hidden="true">
          <path className={clsx(styles.sight, styles.sightLock)} d={d} />
        </g>
      )}
    </>
  );
}

/** The four corner brackets round a box, `margin` outside it, each arm `arm` long: a lock-on on the whole body. */
export function boxBracketPath([bx0, by0, bx1, by1]: readonly number[], margin: number, arm: number) {
  const [x0, y0, x1, y1] = [bx0 - margin, by0 - margin, bx1 + margin, by1 + margin];
  return (
    `M${x0} ${y0 + arm} V${y0} H${x0 + arm} M${x1 - arm} ${y0} H${x1} V${y0 + arm} ` +
    `M${x1} ${y1 - arm} V${y1} H${x1 - arm} M${x0 + arm} ${y1} H${x0} V${y1 - arm}`
  );
}

/** Helpless: one sight round a whole body - its box's corners bracketed, faintly. */
export function OpenSight({ box, margin, arm }: { box: readonly number[]; margin: number; arm: number }) {
  return <path className={fx.openBracket} d={boxBracketPath(box, margin, arm)} data-testid="plate-open" />;
}

interface LeaderProps {
  c: { anchor: readonly [number, number]; name: string };
  /** The callout's column (its text starts here on the right, ends here on the left), its row, and its side. */
  x: number;
  row: number;
  right: boolean;
  /** Where the name sits, from the callout's row. */
  nameDy: number;
  /** The words' scale about the column and row (calloutPlacement's `k`). */
  k: number;
}

/** The transform that scales a callout's words by k about its column and row; none at full size. */
export const scaleAbout = (x: number, row: number, k: number) =>
  k === 1 ? undefined : `translate(${x} ${row}) scale(${k}) translate(${-x} ${-row})`;

/**
 * A callout's leader, its part's dot and the part's name (plan plate-unboxed): the dot stays on the part and a short
 * leader runs straight from it to the name's near edge, wherever the free-spot search stood the words.
 */
export function CalloutLeader({ c, x, row, right, nameDy, k }: LeaderProps) {
  const end = `${x + (right ? -14 : 14)},${row + (nameDy - 20) * k}`;
  return (
    <>
      <polyline className={styles.leader} points={`${c.anchor.join(',')} ${end}`} />
      <circle className={styles.anchorDot} cx={c.anchor[0]} cy={c.anchor[1]} r={16} />
      <text
        className={styles.name}
        x={x}
        y={row + nameDy}
        textAnchor={right ? 'start' : 'end'}
        transform={scaleAbout(x, row, k)}
      >
        {calloutName(c.name)}
      </text>
    </>
  );
}

/**
 * What a plate says under a body that a count cannot: NO LIFE SIGNS once dead - or BANISHED, for a daemon (plan
 * body-map T1) - and PRONE while it lies down. Nothing for a standing living body: the wounds are the panel's own
 * readout, not the plate's (ADR 2026-10-11).
 */
export function statusWords(s: { dead: boolean; prone: boolean }, banished = false) {
  if (s.dead) return banished ? 'BANISHED' : 'NO LIFE SIGNS';
  return s.prone ? 'PRONE' : '';
}

interface StatusLineProps {
  /** The words, or nothing. */
  label: string;
  /** Where the line sits in its plate's frame: left, and its baseline. */
  at: [number, number];
  testid: string;
  labelClass: string;
}

/** The status line under a plate: the words a count cannot say, in the plate's own type; present even when empty. */
export function StatusLine({ label, at: [x, y], testid, labelClass }: StatusLineProps) {
  return (
    <g pointerEvents="none" data-testid={testid}>
      <text className={labelClass} x={x} y={y}>
        {label}
      </text>
    </g>
  );
}

/**
 * What any plate draws when its art cannot load (the load is logged): the frame, named as the plate would be, and a
 * word saying so - never a blank, never a stale drawing.
 */
export function ScanUnavailable({ ariaLabel, className }: { ariaLabel: string; className?: string }) {
  return (
    <svg viewBox="0 0 400 300" className={className} role="img" aria-label={ariaLabel} data-figure="unavailable">
      <text className={styles.unavailable} x="200" y="155" textAnchor="middle">
        SCAN UNAVAILABLE
      </text>
    </svg>
  );
}

/** A plate's size in its panel: filling its cell, or the standalone column width. */
export const plateSize = (fill = false) => (fill ? bodyStyles.svgFill : bodyStyles.svg);
